import React, { useEffect, useRef, useState } from 'react';

import Text from '~components/atoms/Text';

import TabPageContainer from '~components/atoms/TabPageContainer';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';

import * as Styles from './InventoryReport.module.scss';
import InventoryReportCardsSummary from './InventoryReportCardsSummary';
import InventoryReportSummarySearchAndFilterBar from './InventoryReportSummarySearchAndFilterBar';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import InventoryReportSearchAndFilterBar from './InventoryReportSearchAndFilterBar';
import DownloadCSV from '~components/molecules/DownloadCSV';
import UtilFunctions from '~utils/functions';
import combinedActions from '~redux/combinedActions';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { ReportInventoryReportSelectedTab, MetalTypeEnum, FundTypeEnum, InventoryReportTabsEnum } from '~utils/enums';
import InventoryReportTable from './InventoryReceived/InventoryReceivedReportTable';
import TabSelector from '~components/atoms/TabSelector';
import { ReportInventoryReportTabs } from '~utils/constants';
import TabDisplay from '~components/atoms/TabDisplay';
import InventoryDeliveredReportTable from './InventoryDelivered/InventoryDeliveredReportTable';
import InventoryReceivedReportTable from './InventoryReceived/InventoryReceivedReportTable';
import InventoryBranchReportTable from './InventoryBranch/InventoryBranchReportTable';

const InventoryReport = () => {
    const dispatch = useAppDispatch();
    const {
        Misc: {
            dateTimeFormat,
        },
    } = UtilFunctions;

    const isReadyToExportRef = useRef(false);
    const prevLoadingRef = useRef(false);

    const inventoryReceivedReportListingForExport = useAppSelector(state => state.report.reportExportInventoryReceivedReportListingForExport);
    const inventoryDeliveredReportListingForExport = useAppSelector(state => state.report.reportExportInventoryDeliveredReportListingForExport);
    const inventoryBranchReportListingForExport = useAppSelector(state => state.report.reportExportInventoryBranchReportListingForExport);

    const getExportInventoryReceivedReportListingForExportIsLoading = useAppSelector(state => state.report.loadings.exportInventoryReceivedReportListing);
    const getExportInventoryDeliveredReportListingForExportIsLoading = useAppSelector(state => state.report.loadings.exportInventoryDeliveredReportListing);
    const getExportInventoryBranchReportListingForExportIsLoading = useAppSelector(state => state.report.loadings.exportInventoryBranchReportListing);

    const inventoryReceivedReportListingIndex = useAppSelector(state => state.report.inventoryReport.inventoryReceivedReportTransactionListing.index);
    const inventoryDeliveredReportListingIndex = useAppSelector(state => state.report.inventoryReport.inventoryDeliveredReportTransactionListing.index);
    const inventoryBranchReportListingIndex = useAppSelector(state => state.report.inventoryReport.inventoryBranchReportListing.index);

    const inventorySummaryDetails = useAppSelector((state) => state.report.inventoryReport.inventoryReportCardSummary);

    const selectedInventoryReportTab = useAppSelector((state) => state.report.inventoryReport.selectedInventoryReportTab);

    const inventoryReportListingForExport = selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
        ? inventoryReceivedReportListingForExport
        : inventoryDeliveredReportListingForExport;

    const setSelectedInventoryTab = (value: ReportInventoryReportSelectedTab) => {
        dispatch(combinedActions.reportSetSelectedInventoryReportTab(value));
    };

    const exportExportInventoryReportListing = () => {
        isReadyToExportRef.current = true; 
        
        if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived) {
            dispatch(combinedActions.reportGetInventoryReceivedReportExportAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered) {
            dispatch(combinedActions.reportGetInventoryDeliveredReportExportAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory) {
            dispatch(combinedActions.reportGetInventoryBranchReportExportAttempt());
        }
    };

    const clearInventoryReportListingForExport = () => {
        if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived) {
            dispatch(combinedActions.reportClearInventoryReceivedReportListingForExport())
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered) {
            dispatch(combinedActions.reportClearInventoryDeliveredReportListingForExport())
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory) {
            dispatch(combinedActions.reportClearInventoryBranchReportListingForExport());
        }
    };

    const [isExportingInventoryReportListing, setIsExportingInventoryReportListing] = useState(false);

    const activeListingIndex =selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
        ? inventoryReceivedReportListingIndex
        : selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered
            ? inventoryDeliveredReportListingIndex
            : inventoryBranchReportListingIndex;

    useEffect(() => {
        dispatch(combinedActions.reportGetInventoryReportCardSummaryAttempt());
    }, [dispatch]);

    useEffect(() => {
        if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived) {
            dispatch(combinedActions.reportGetInventoryReceivedReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered) {
            dispatch(combinedActions.reportGetInventoryDeliveredReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory) {
            dispatch(combinedActions.reportGetInventoryBranchReportListingAttempt());
        }
    }, [dispatch, selectedInventoryReportTab, activeListingIndex]);

    const receivedHeader = [
        'PRODUCT NAME',
        'METAL TYPE',
        'BRANCH',
        'RECEIVED FROM',
        'SERIAL NUMBER',
        'QUANTITY',
        'TOTAL WEIGHT (G)',
        'FUND TYPE',
        'RECEIVED AT',
    ];

    const deliveredHeader = [
        'PRODUCT NAME',
        'METAL TYPE',
        'BRANCH',
        'DELIVERY TO',
        'SERIAL NUMBER',
        'QUANTITY',
        'TOTAL WEIGHT (G)',
        'FUND TYPE',
        'DELIVERED AT',
    ];

    const branchHeader = [
        'PRODUCT NAME',
        'METAL TYPE',
        'BRANCH',
        'SERIAL NUMBER',
        'QUANTITY',
        'TOTAL WEIGHT (G)',
        'FUND TYPE',
    ];

    const formatSerialNumbers = (serialNumbers?: string[] | null) =>
        (serialNumbers || []).filter(Boolean).join('|') || '-';

    const formatWeight = (weight?: number | null) =>
        weight != null ? Decimal(weight).toFixed(3) : '-';

    const receivedData = inventoryReceivedReportListingForExport.map(transaction => ({
        productName: transaction.productName || '-',
        metalType: MetalTypeEnum[transaction.metalType] || '-',
        branch: transaction.branch?.branchName || '-',
        receivedFrom: transaction.receivedFrom?.branchName || '-',
        serialNumbers: formatSerialNumbers(transaction.serialNumbers),
        quantity: transaction.quantity ?? '-',
        totalWeight: formatWeight(transaction.totalWeight),
        fundType: FundTypeEnum[transaction.fundType] || '-',
        receivedAt: transaction.receivedAt
            ? dateTimeFormat(transaction.receivedAt)
            : '-',
    }));

    const deliveredData = inventoryDeliveredReportListingForExport.map(transaction => ({
        productName: transaction.productName || '-',
        metalType: MetalTypeEnum[transaction.metalType] || '-',
        branch: transaction.branch?.branchName || '-',
        deliveredTo: transaction.deliveredTo?.branchName || '-',
        serialNumbers: formatSerialNumbers(transaction.serialNumbers),
        quantity: transaction.quantity ?? '-',
        totalWeight: formatWeight(transaction.totalWeight),
        fundType: FundTypeEnum[transaction.fundType] || '-',
        deliveredAt: transaction.deliveredAt
            ? dateTimeFormat(transaction.deliveredAt)
            : '-',
    }));

    const branchData = inventoryBranchReportListingForExport.map(transaction => ({
        productName: transaction.productName || '-',
        metalType: MetalTypeEnum[transaction.metalType] || '-',
        branch: transaction.branchName || '-',
        serialNumbers: formatSerialNumbers(transaction.serialNumbers),
        quantity: transaction.quantity ?? '-',
        totalWeight: formatWeight(transaction.totalWeight),
        fundType: FundTypeEnum[transaction.fundType] || '-',
    }));

    const listHeader =
        selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
            ? receivedHeader
            : selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered
                ? deliveredHeader
                : branchHeader;

    const listData =
        selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
            ? receivedData
            : selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered
                ? deliveredData
                : branchData;

    const handleExportGoldTransactionListingCleanup = () => {
        clearInventoryReportListingForExport();
        setIsExportingInventoryReportListing(false);
        isReadyToExportRef.current = false;
    };

    const tabs = [
        {
            name: InventoryReportTabsEnum[InventoryReportTabsEnum['Inventory Received']],
            element: <InventoryReceivedReportTable />,
        },
        {
            name: InventoryReportTabsEnum[InventoryReportTabsEnum['Inventory Delivered']],
            element: <InventoryDeliveredReportTable />,
        },
        {
            name: InventoryReportTabsEnum[InventoryReportTabsEnum['Branch Inventory']],
            element: <InventoryBranchReportTable />,
        },
    ];

    const exportButtonLabel = selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
        ? 'Export Inventory Received to CSV'
        : selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered
            ? 'Export Inventory Delivered to CSV'
            : 'Export Branch Inventory to CSV';

    const isExportLoading = selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived
        ? getExportInventoryReceivedReportListingForExportIsLoading
        : selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered
            ? getExportInventoryDeliveredReportListingForExportIsLoading
            : getExportInventoryBranchReportListingForExportIsLoading;

    useEffect(() => {
        if (prevLoadingRef.current && !isExportLoading && isReadyToExportRef.current
        ) {
            if (listData.length === 0) {
                const csv = listHeader.join(',') + '\r\n';
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = `${dayjs().format('DD-MM-YYYY')} ${exportButtonLabel}.csv`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                handleExportGoldTransactionListingCleanup();
            }
        }

        prevLoadingRef.current = isExportLoading;
    }, [isExportLoading]);
    
    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Emas Account-I / Silver Account-I'
                    className={Styles.title}
                    isBold
                />
            </TabPageHeaderWrapper>

            <InventoryReportSummarySearchAndFilterBar />
            <InventoryReportCardsSummary summaryData={inventorySummaryDetails} />

            <TabPageHeaderWrapper>
                <Text
                    label='Emas-I / Silver-I'
                    className={Styles.title}
                    isBold
                />

                <div className={Styles['action-container']}>
                    <DownloadCSV
                        exportTestId='salesReport-transaction-export-button'
                        buttonLabel={exportButtonLabel}
                        header={listHeader}
                        data={listData}
                        fileName={`${dayjs().format('DD-MM-YYYY')} ${exportButtonLabel}`}
                        onClick={exportExportInventoryReportListing}
                        cleanup={handleExportGoldTransactionListingCleanup}
                        isLoading={isExportLoading || isExportingInventoryReportListing}
                    />
                </div>
            </TabPageHeaderWrapper>

            <InventoryReportSearchAndFilterBar />
            <TabSelector
                selectedTab={selectedInventoryReportTab}
                setSelectedTab={setSelectedInventoryTab}
                tabs={ReportInventoryReportTabs}
            >
                <TabDisplay
                    tabs={tabs}
                    currentTab={InventoryReportTabsEnum[selectedInventoryReportTab]}
                    containerStyle={Styles['tab-display']}
                />
            </TabSelector>
        </TabPageContainer>
    );
};

export default InventoryReport;
